import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { TypedMemo } from '../common/types';
import { useCreateAccountMutation } from '../../redux/api/fymAPI';
import { slugifyArtistName } from './slugifyArtistName';
import { readM24Error } from '../music24/utils/parseM24LimitError';
import { useDropAccountIdFromUrl } from './useDropAccountIdFromUrl';

interface AddArtistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddArtistModalComponent: React.FC<AddArtistModalProps> = ({ isOpen, onClose }) => {
  const intl = useIntl();
  const [name, setName] = useState('');
  const [createAccount, { isLoading, error, reset }] = useCreateAccountMutation();
  const dropAccountIdFromUrl = useDropAccountIdFromUrl();

  const close = useCallback(() => {
    setName('');
    reset();
    onClose();
  }, [onClose, reset]);

  const submit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const slug = `${slugifyArtistName(name)}${Math.floor(Math.random() * 1000)}`;
      createAccount({ name: name.trim(), slug })
        .unwrap()
        .then(() => {
          // `createAccount`'s `onQueryStarted` already dispatched `setAccountId` for the new
          // artist — drop the old `?accountId=` too, or the resolver in `SmartLinksContainer`
          // reads it back out of the URL on the next render and switches straight back.
          dropAccountIdFromUrl();
          close();
        })
        .catch((e: unknown) => {
          // `customBaseQuery`'s catch already dispatched the global upgrade prompt for this
          // error (it calls `dispatchM24LimitPrompt` on every RTK Query failure) — just close.
          if (readM24Error(e)?.code === 'M24_LIMIT_ARTISTS') {
            close();
          }
        });
    },
    [close, createAccount, dropAccountIdFromUrl, name]
  );

  const isLimitError = readM24Error(error)?.code === 'M24_LIMIT_ARTISTS';

  return (
    <Modal isOpen={isOpen} toggle={close} centered>
      <Form onSubmit={submit}>
        <ModalHeader toggle={close}>
          {intl.formatMessage({ id: 'app.sml.artistSwitcher.addTitle', defaultMessage: 'Add an artist' })}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="add-artist-name">
              {intl.formatMessage({ id: 'app.sml.artistSwitcher.nameLabel', defaultMessage: 'Artist name' })}
            </Label>
            <Input id="add-artist-name" value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          </FormGroup>
          <p className="text-muted small mb-0">
            {intl.formatMessage({
              id: 'app.sml.artistSwitcher.addHelper',
              defaultMessage:
                'The artist joins this workspace. You can switch to it from the list at the top of the sidebar.',
            })}
          </p>
          {error !== undefined && !isLimitError && (
            <Alert color="danger" className="mb-0">
              {intl.formatMessage({
                id: 'app.sml.artistSwitcher.addError',
                defaultMessage: 'We could not add this artist. Try again.',
              })}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="link" onClick={close}>
            {intl.formatMessage({ id: 'app.sml.settings.account.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button color="primary" type="submit" disabled={isLoading || name.trim() === ''}>
            {isLoading && <Spinner size="sm" className="me-2" aria-hidden="true" />}
            {intl.formatMessage({ id: 'app.sml.artistSwitcher.addSubmit', defaultMessage: 'Add artist' })}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export const AddArtistModal = TypedMemo(AddArtistModalComponent);
